/**
 * platform.ts
 * -----------
 * Helpers de detecção de plataforma (web vs Capacitor iOS/Android).
 * Usado pra alternar entre Stripe (web) e StoreKit/RevenueCat (iOS nativo).
 */

import { Capacitor } from "@capacitor/core";

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

type CapacitorWindow = Window & {
  Capacitor?: CapacitorGlobal;
  CapacitorAndroid?: unknown;
  webkit?: {
    messageHandlers?: {
      bridge?: unknown;
    };
  };
};

function getNativeBridgePlatform(): "ios" | "android" | null {
  try {
    const protocol = window.location?.protocol;
    const cap = getCapacitor();
    const platform = Capacitor.getPlatform?.() || cap?.getPlatform?.();
    const win = window as CapacitorWindow;

    if (platform === "ios" || platform === "android") return platform;

    // Fallback mais confiável quando o bundle roda via server.url/HTTPS no WKWebView:
    // o protocolo pode ser https:, mas a bridge nativa continua exposta.
    if (win.CapacitorAndroid) return "android";
    if (win.webkit?.messageHandlers?.bridge) return "ios";

    // Fallback: bridge protocol presente mas getPlatform() ainda não respondeu.
    // Usa User-Agent pra distinguir iOS vs Android em vez de assumir "ios".
    if (protocol === "capacitor:" || protocol === "ionic:") {
      const ua = navigator.userAgent || "";
      if (/Android/i.test(ua)) return "android";
      if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
      // Sem sinal claro no UA: retorna null (isNativePlatform já cobre via Capacitor.isNativePlatform()).
      return null;
    }
    return null;
  } catch {
    return null;
  }
}

function getCapacitor(): CapacitorGlobal | null {
  try {
    return (window as CapacitorWindow).Capacitor ?? null;
  } catch {
    return null;
  }
}

export function isNativePlatform(): boolean {
  try {
    return (
      Capacitor.isNativePlatform() ||
      !!getCapacitor()?.isNativePlatform?.() ||
      getNativeBridgePlatform() !== null
    );
  } catch {
    return false;
  }
}

export function getPlatform(): "ios" | "android" | "web" {
  try {
    const bridgePlatform = getNativeBridgePlatform();
    if (bridgePlatform) return bridgePlatform;
    const p = Capacitor.getPlatform?.() || getCapacitor()?.getPlatform?.();
    if (p === "ios" || p === "android") return p;
    return "web";
  } catch {
    return "web";
  }
}

export function isIOSNative(): boolean {
  return isNativePlatform() && getPlatform() === "ios";
}

export function isAndroidNative(): boolean {
  return isNativePlatform() && getPlatform() === "android";
}

export function isWebPlatform(): boolean {
  return !isNativePlatform();
}

/**
 * Whether to use IAP (RevenueCat → StoreKit/Play Billing) instead of Stripe checkout.
 * iOS e Android nativos. Web segue Stripe.
 */
export function shouldUseIAP(): boolean {
  return isIOSNative() || isAndroidNative();
}

/** URL pra abrir a tela de gerenciar assinatura no store nativo. */
export function getManageSubscriptionUrl(): string | null {
  if (isIOSNative()) return "itms-apps://apps.apple.com/account/subscriptions";
  if (isAndroidNative()) return "https://play.google.com/store/account/subscriptions";
  return null;
}

/** Nome curto do store ativo (pra copy condicional). */
export function getStoreName(): "App Store" | "Google Play" | "Web" {
  if (isIOSNative()) return "App Store";
  if (isAndroidNative()) return "Google Play";
  return "Web";
}

/* ──────────────────────────────────────────────
   iOS Safari (web) helpers — usado pelo banner
   de "Adicionar à Tela de Início" para push.
   ────────────────────────────────────────────── */

/** Detecta Safari iOS (web), independente de Capacitor. */
export function isIOS(): boolean {
  try {
    const ua = navigator.userAgent || "";
    const isIPad =
      /iPad/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    return /iPhone|iPod/.test(ua) || isIPad;
  } catch {
    return false;
  }
}

/** Detecta se app está rodando como PWA standalone (após "Adicionar à Tela de Início"). */
export function isStandalonePWA(): boolean {
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    const nav = navigator as Navigator & { standalone?: boolean };
    return !!nav.standalone;
  } catch {
    return false;
  }
}

export type PushIOSStatus =
  | "not_ios"
  | "ios_needs_install"
  | "ios_ready"
  | "ios_granted"
  | "ios_denied";

/**
 * Retorna status do push pra iOS Safari (web).
 *  - "not_ios"           → não é iOS (deixa flow padrão Web Push rolar)
 *  - "ios_needs_install" → iOS Safari não-standalone (precisa Add to Home)
 *  - "ios_ready"         → iOS standalone, permission default
 *  - "ios_granted"       → iOS standalone, permission granted
 *  - "ios_denied"        → iOS standalone, permission denied
 *
 * No Capacitor nativo retorna "not_ios" (flow nativo é separado).
 */
export function getPushIOSStatus(
  permission: NotificationPermission | "default" | "unsupported",
): PushIOSStatus {
  if (isNativePlatform()) return "not_ios";
  if (!isIOS()) return "not_ios";
  if (!isStandalonePWA()) return "ios_needs_install";
  if (permission === "granted") return "ios_granted";
  if (permission === "denied") return "ios_denied";
  return "ios_ready";
}
